import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleLogo, EnvelopeSimple, X, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import AppleSignInButton from "./AppleSignInButton";

/**
 * LoginPromptModal — soft account gate.
 *
 * Shown when an anonymous user takes an action that benefits from an
 * account (e.g. their 3rd follow, or any future feature that genuinely
 * needs cross-device persistence). Three sign-in paths:
 *   1. Continue with Google — full-page redirect via Emergent Auth.
 *   2. Email me a sign-in link — Resend-backed magic link.
 *   3. More options — collapses open to the existing email/password form
 *      (preserves access for users with classic accounts).
 *
 * Tone: feels like unlocking a feature, never like being locked out.
 *
 * Props
 *   open: boolean
 *   onClose: () => void
 *   title: string (default "Don't miss your favorite trucks")
 *   body: string (default copy)
 *   onSuccess: optional() → fired AFTER the auth state flips, so the
 *              caller can re-attempt the original gated action and
 *              show "you're now following X" confirmation.
 */
const DEFAULT_TITLE = "Don't miss your favorite trucks";
const DEFAULT_BODY =
  "Create a free Trackr account to follow trucks, save favorites, and get alerts when they're nearby.";

export default function LoginPromptModal({
  open,
  onClose,
  title = DEFAULT_TITLE,
  body = DEFAULT_BODY,
  onSuccess,
}) {
  const { requestMagicLink, login } = useAuth();
  const [busy, setBusy] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pwdEmail, setPwdEmail] = useState("");
  const [pwdPassword, setPwdPassword] = useState("");

  const handleClose = () => {
    if (busy) return;
    setMagicEmail("");
    setMagicSent(false);
    setMoreOpen(false);
    setPwdEmail("");
    setPwdPassword("");
    onClose?.();
  };

  const handleGoogle = () => {
    // Stash the post-auth landing URL + a flag so AuthCallback knows to
    // close any reopened modal and trigger the original action. Using
    // sessionStorage so it survives the round-trip but doesn't leak
    // across tabs.
    try {
      sessionStorage.setItem("tt:auth:return-to", window.location.pathname + window.location.search);
      sessionStorage.setItem("tt:auth:gated", "1");
    } catch {}
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const handleMagicLink = async (e) => {
    e?.preventDefault?.();
    if (!magicEmail.trim()) return;
    setBusy(true);
    const res = await requestMagicLink(magicEmail.trim());
    setBusy(false);
    if (res.ok) {
      setMagicSent(true);
    } else {
      toast.error(res.error || "Couldn't send the sign-in link");
    }
  };

  const handlePwdLogin = async (e) => {
    e?.preventDefault?.();
    if (!pwdEmail.trim() || !pwdPassword) return;
    setBusy(true);
    const res = await login({ email: pwdEmail.trim(), password: pwdPassword });
    setBusy(false);
    if (res.ok) {
      onSuccess?.();
      handleClose();
    } else if (res.invitePending) {
      toast.error("Account setup not finished — check your email for the invite.");
    } else {
      toast.error(res.error || "Couldn't sign in. Check your email + password.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
            data-testid="login-prompt-backdrop"
          />
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed z-[61] left-3 right-3 bottom-3 sm:left-1/2 sm:bottom-auto sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-sm sm:right-auto rounded-2xl border border-border bg-card shadow-2xl p-5"
            role="dialog"
            aria-labelledby="login-prompt-title"
            data-testid="login-prompt-modal"
          >
            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="absolute top-3 right-3 h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50"
              aria-label="Dismiss"
              data-testid="login-prompt-close"
            >
              <X weight="bold" className="h-3.5 w-3.5" />
            </button>

            <div className="flex items-start gap-3 pr-6">
              <div className="flex-none h-10 w-10 grid place-items-center rounded-full bg-primary/10 text-primary">
                <Sparkle weight="duotone" className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="login-prompt-title" className="font-display font-bold text-base leading-snug" data-testid="login-prompt-title">
                  {title}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{body}</p>
              </div>
            </div>

            {/* Provider buttons */}
            {!magicSent && (
              <div className="mt-4 space-y-2">
                <AppleSignInButton onSuccess={onClose} />
                <button
                  type="button"
                  onClick={handleGoogle}
                  disabled={busy}
                  className="w-full h-10 rounded-full bg-foreground text-background text-sm font-bold inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
                  data-testid="login-prompt-google"
                >
                  <GoogleLogo weight="bold" className="h-4 w-4" />
                  Continue with Google
                </button>

                <form onSubmit={handleMagicLink} className="space-y-2" data-testid="login-prompt-magic-form">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-full border border-border bg-card focus-within:ring-2 focus-within:ring-primary/40">
                      <EnvelopeSimple weight="duotone" className="h-4 w-4 text-muted-foreground" />
                      <input
                        type="email"
                        required
                        autoComplete="email"
                        value={magicEmail}
                        onChange={(e) => setMagicEmail(e.target.value)}
                        placeholder="you@example.com"
                        disabled={busy}
                        className="flex-1 bg-transparent outline-none text-sm"
                        data-testid="login-prompt-magic-email"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={busy || !magicEmail.trim()}
                      className="h-10 px-3 rounded-full border border-border text-xs font-semibold hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1"
                      data-testid="login-prompt-magic-send"
                    >
                      {busy ? "…" : <>Send link <ArrowRight weight="bold" className="h-3.5 w-3.5" /></>}
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground text-center">
                    We'll email you a one-time link. No password needed.
                  </p>
                </form>
              </div>
            )}

            {/* Magic-link sent confirmation */}
            {magicSent && (
              <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm" data-testid="login-prompt-magic-sent">
                <div className="font-semibold">Check your inbox</div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  We sent a sign-in link to <span className="font-semibold">{magicEmail}</span>. Tap it on this device to finish signing in. The link expires in 15 minutes.
                </p>
              </div>
            )}

            {/* More options (existing email + password) */}
            {!magicSent && (
              <div className="mt-4 pt-3 border-t border-border">
                <button
                  type="button"
                  onClick={() => setMoreOpen((s) => !s)}
                  className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  data-testid="login-prompt-more-toggle"
                >
                  {moreOpen ? "Hide" : "More options"} (existing account password)
                </button>
                {moreOpen && (
                  <form onSubmit={handlePwdLogin} className="mt-2 space-y-2" data-testid="login-prompt-pwd-form">
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={pwdEmail}
                      onChange={(e) => setPwdEmail(e.target.value)}
                      placeholder="Email"
                      disabled={busy}
                      className="w-full h-9 px-3 rounded-full border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <input
                      type="password"
                      required
                      autoComplete="current-password"
                      value={pwdPassword}
                      onChange={(e) => setPwdPassword(e.target.value)}
                      placeholder="Password"
                      disabled={busy}
                      className="w-full h-9 px-3 rounded-full border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <button
                      type="submit"
                      disabled={busy || !pwdEmail.trim() || !pwdPassword}
                      className="w-full h-9 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-50"
                      data-testid="login-prompt-pwd-submit"
                    >
                      {busy ? "Signing in…" : "Sign in"}
                    </button>
                    <Link
                      to="/forgot-password"
                      className="block text-center text-[11px] text-muted-foreground hover:text-foreground"
                      onClick={handleClose}
                    >
                      Forgot password?
                    </Link>
                  </form>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="mt-3 w-full h-9 rounded-full text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50"
              data-testid="login-prompt-not-now"
            >
              Not now
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
